import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { TaskSelector } from "./TaskSelector";

interface AddDelegationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    employeeId: string;
    delegation?: any; // If passed, it's edit mode
}

export const AddDelegationModal = ({ isOpen, onClose, onSave, employeeId, delegation }: AddDelegationModalProps) => {
    const { token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        task: "",
        description: "",
        competenceLevel: "Direct",
        category: "",
        targetDate: "",
        notes: ""
    });
    const [isQuickSave, setIsQuickSave] = useState(false);

    useEffect(() => {
        if (delegation) {
            setFormData({
                task: delegation.task,
                description: delegation.description || "",
                competenceLevel: delegation.competenceLevel,
                category: delegation.category || "",
                targetDate: delegation.targetDate ? new Date(delegation.targetDate).toISOString().split('T')[0] : "",
                notes: delegation.notes || ""
            });
        } else {
            setFormData({
                task: "",
                description: "",
                competenceLevel: "Direct",
                category: "",
                targetDate: "",
                notes: ""
            });
        }
        setIsQuickSave(false);
    }, [delegation, isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSelectChange = (field: string, value: string) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleTaskChange = (value: string) => {
        setFormData({ ...formData, task: value });
    };

    const handleSubmit = async () => {
        if (!formData.task) {
            toast.error("Task name is required");
            return;
        }

        try {
            setIsLoading(true);

            // Handle Quick Save
            if (isQuickSave) {
                try {
                    await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify({ name: formData.task, type: "Task" })
                    });
                } catch (err) {
                    console.error("Quick save failed", err);
                }
            }

            const url = delegation
                ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation/${delegation.id}`
                : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation`;

            const method = delegation ? "PATCH" : "POST";

            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    employeeId
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(delegation ? "Delegation updated" : "Delegation added");
                onSave();
                onClose();
            } else {
                toast.error(data.message || "Error saving delegation");
            }
        } catch (error) {
            console.error("Error saving delegation:", error);
            toast.error("Failed to save delegation");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{delegation ? "Edit Delegation Task" : `Add Delegation Task`}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="task">Task / Activity / Decision *</Label>
                        <TaskSelector
                            value={formData.task}
                            onChange={handleTaskChange}
                            isQuickSave={isQuickSave}
                            onQuickSaveChange={setIsQuickSave}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            placeholder="Describe the task or responsibility..."
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="competenceLevel">Competence Level</Label>
                            <Select
                                value={formData.competenceLevel}
                                onValueChange={(val) => handleSelectChange("competenceLevel", val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Level" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Direct">Direct</SelectItem>
                                    <SelectItem value="Coach">Coach</SelectItem>
                                    <SelectItem value="Support">Support</SelectItem>
                                    <SelectItem value="Delegate">Delegate</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="category">Category</Label>
                            <Input
                                id="category"
                                placeholder="e.g. Finance, Operations"
                                value={formData.category}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="targetDate">Target Date</Label>
                        <Input
                            id="targetDate"
                            type="date"
                            value={formData.targetDate}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="notes">Notes / Comments</Label>
                        <Textarea
                            id="notes"
                            placeholder="Add any additional notes or progress comments..."
                            value={formData.notes}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleSubmit} disabled={isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {delegation ? "Save Changes" : "Save Task"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
